import React from 'react'
import i18n from '../i18n.js'
import { S } from '../locales/strings.js'

interface Props {
  children: React.ReactNode
  fallback?: React.ReactNode
  name?: string  // for logging context
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(`[ErrorBoundary:${this.props.name ?? 'unknown'}]`, error, info.componentStack)
    // Forward to analytics if available
    try {
      const analytics = (window as unknown as Record<string, unknown>)['__analytics'] as { logError?: (e: Error, ctx: string) => void } | undefined
      analytics?.logError?.(error, this.props.name ?? 'unknown')
    } catch {
      // Swallow any errors from analytics forwarding
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children

    if (this.props.fallback) return this.props.fallback

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9000,
          background: 'linear-gradient(160deg, #08060F 0%, #1a0a2e 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          textAlign: 'center',
        }}
        role="alert"
        aria-live="assertive"
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ color: '#E53935', fontSize: 22, marginBottom: 8, fontFamily: 'system-ui, sans-serif' }}>
          {i18n.t(S.ERROR_BOUNDARY_HEADING)}
        </h2>
        <p style={{ color: '#aaa', fontSize: 14, marginBottom: 8, maxWidth: 320, fontFamily: 'system-ui, sans-serif' }}>
          {this.props.name ? `Fehler in: ${this.props.name}` : 'Ein unerwarteter Fehler ist aufgetreten.'}
        </p>
        {this.state.error !== null && (
          <details style={{ marginBottom: 20 }}>
            <summary style={{ color: '#666', fontSize: 12, cursor: 'pointer' }}>Details</summary>
            <pre
              style={{
                color: '#888',
                fontSize: 10,
                textAlign: 'left',
                background: 'rgba(0,0,0,0.5)',
                padding: 8,
                borderRadius: 6,
                maxWidth: 340,
                overflow: 'auto',
                marginTop: 8,
              }}
            >
              {this.state.error.message}
            </pre>
          </details>
        )}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={this.handleReset}
            style={{
              background: '#C9A84C',
              color: '#000',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 'bold',
              cursor: 'pointer',
              minHeight: 44,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Nochmal versuchen
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'transparent',
              color: '#aaa',
              border: '1px solid #444',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              cursor: 'pointer',
              minHeight: 44,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            {i18n.t(S.ERROR_BOUNDARY_RELOAD)}
          </button>
        </div>
      </div>
    )
  }
}

/**
 * Lightweight HOC that wraps a component in an ErrorBoundary.
 * Usage: const SafeMyScreen = withErrorBoundary(MyScreen, 'MyScreen')
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  name: string
): React.ComponentType<P> {
  function WrappedComponent(props: P): React.ReactElement {
    return (
      <ErrorBoundary name={name}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
  WrappedComponent.displayName = `WithErrorBoundary(${name})`
  return WrappedComponent
}

export default ErrorBoundary
